import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  fetchMailingsConsentList,
  useMailingsCapabilities,
  useMailingsConsentRunStatus,
  useMailingConsentPreview,
  useMailingConsentRecheck,
  useMailingConsentSendAll,
  useMailingConsentTestSend,
  type MailingsCapabilitiesDto,
  type MailingConsentRecipientDto,
  type MailingConsentRunStatusDto,
  type MailingConsentSummaryDto,
  type MailingDeliveryFilter,
} from '../../api/mailings';
import { Badge, Button, EmptyState, ErrorState, Field, Input, Modal, Pagination, Select, Spinner } from '../../components/ui';
import { TableWrap } from '../../components/TableWrap';
import { useQueryParams } from '../../lib/query-params';
import {
  DELIVERY_FILTERS,
  EMPTY_CONSENT_SUMMARY,
  LOGIC_DESCRIPTION_CONSENT,
  MAILING_TYPE_LABEL_CONSENT,
  PARAM,
  TRACK_LABELS,
  formatCapabilities,
  formatConsentRunProgress,
  formatHistoryLine,
  formatLastSend,
  parseDeliveryParam,
  parsePageParam,
} from './mailings-model';

/**
 * The THIRD fixed mailing's panel (contract #32.14–#32.19, ADDITIVE v1.9.55,
 * T.Z182): «Ошибка подписи договора: не указано согласие родителей для
 * заявителей от 14 до 18 лет» — the error letter to the 14–17-year-olds whose
 * contract was signed WITHOUT the parental-consent tick.
 *
 * The SECOND fixed panel's controls, one surface over a different audience: the
 * stated inclusion logic (the register's five-axis page, restated in the words
 * the register's filters speak), the stat-card counters, the test letter that
 * never journals, the confirmed paced «Отправить всем», the sandboxed preview
 * and the honest delivery states. THE LETTER carries TWO links since v1.9.57
 * (T.Z185, the owner's 24.09 ruling): «личный кабинет» → the T.Z124 welcome page
 * of the child's track (ALWAYS resolves), «Перейти на страницу курса» → the ЕПГУ
 * course page of his OWN programme × audience (`gosuslugiTestLink`) — and THAT
 * one can fail, so readiness is name + e-mail + the link.
 */

const PAGE_SIZE = 20;

export function ConsentUnsignedPanel() {
  const { params, setParams } = useQueryParams();
  const delivery = parseDeliveryParam(params[PARAM.DELIVERY] ?? null);
  const page = parsePageParam(params[PARAM.PAGE] ?? null);

  const list = useQuery({
    queryKey: ['mailings-consent', 'list', delivery ?? 'ALL', page],
    queryFn: ({ signal }) => fetchMailingsConsentList({ delivery, page, pageSize: PAGE_SIZE }, signal),
  });

  const runStatus = useMailingsConsentRunStatus();

  const summary = list.data?.summary ?? EMPTY_CONSENT_SUMMARY;
  const rows = list.data?.data ?? [];

  const setFilter = (next: { delivery?: MailingDeliveryFilter }) => {
    setParams({ [PARAM.DELIVERY]: next.delivery ?? null, [PARAM.PAGE]: null });
  };

  return (
    <div className="mailings-classic" data-testid="mailings-consent-panel">
      <section className="mailings-desc card" data-testid="mailings-consent-logic">
        <div className="mailings-filters mailings-filters--plain">
          <h2>Быстрая рассылка: {MAILING_TYPE_LABEL_CONSENT}</h2>
        </div>
        <p>{LOGIC_DESCRIPTION_CONSENT}</p>
      </section>

      <ConsentSummaryChips summary={summary} />

      <CapabilitiesLine />

      <TestSendCard rows={rows} />

      <SendAllCard runStatus={runStatus.data?.data ?? null} notSent={summary.delivery.notSent} notReady={summary.notReady} />

      <section className="mailings-list card card--flush" data-testid="mailings-consent-list">
        <div className="mailings-filters">
          <h2>Список получателей</h2>
          <Field label="Доставка" htmlFor="mailings-consent-delivery">
            <Select
              id="mailings-consent-delivery"
              value={delivery ?? ''}
              onChange={(event) => setFilter({ delivery: (event.target.value || undefined) as MailingDeliveryFilter | undefined })}
              data-testid="mailings-consent-delivery"
            >
              {DELIVERY_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.value === ''
                    ? `Все (${summary.total})`
                    : `${option.label} (${summary.delivery[option.countKey]})`}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        {list.isPending ? (
          <Spinner label="Загружаем получателей" />
        ) : list.isError ? (
          <ErrorState message={String(list.error)} onRetry={() => void list.refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState title="Никого" hint="Под текущие фильтры не попал ни один ребёнок." />
        ) : (
          <RecipientsTable
            rows={rows}
            page={list.data?.meta.page ?? page}
            total={list.data?.meta.total ?? 0}
            onPage={(next) => setParams({ [PARAM.PAGE]: next === 1 ? null : String(next) })}
          />
        )}
      </section>
    </div>
  );
}

/** The accent tone of one summary tile — the other panels' own vocabulary. */
type ChipTone = 'brand' | 'neutral' | 'ok' | 'bad' | 'warn';

/** The counters as stat cards, the other panels' grammar — «не готовы к
 * отправке» counts the children no letter can reach (no e-mail, no usable name). */
function ConsentSummaryChips({ summary }: { summary: MailingConsentSummaryDto }) {
  const tiles: ReadonlyArray<{ key: string; value: number; label: string; tone: ChipTone; hint?: string }> = [
    { key: 'total', value: summary.total, label: 'всего получателей', tone: 'brand' },
    { key: 'ready', value: summary.ready, label: 'готовы к отправке', tone: 'ok' },
    {
      key: 'not-ready',
      value: summary.notReady,
      label: 'не готовы к отправке',
      tone: 'warn',
      hint: 'Нет e-mail, нет разобранного имени или не определилась ссылка на курс на Госуслугах — письмо такой ребёнок не получит.',
    },
    { key: 'not-sent', value: summary.delivery.notSent, label: 'не получил письмо', tone: 'neutral' },
    { key: 'accepted', value: summary.delivery.accepted, label: 'принято', tone: 'neutral' },
    { key: 'delivered', value: summary.delivery.delivered, label: 'доставлено', tone: 'ok' },
    { key: 'failed', value: summary.delivery.failed, label: 'ошибка доставки', tone: 'bad' },
    { key: 'ai', value: summary.tracks.ai, label: 'программы ИИ', tone: 'brand' },
    { key: 'python', value: summary.tracks.python, label: 'программы Python', tone: 'brand' },
    {
      key: 'unresolved',
      value: summary.tracks.unresolved,
      label: 'трек не определён',
      tone: 'neutral',
      hint: 'Программа не распозналась — письмо уйдёт на Python-страницу того трека.',
    },
  ];
  return (
    <section className="mailings-summary" data-testid="mailings-consent-summary">
      {tiles.map((tile) => (
        <div
          key={tile.key}
          className={`mailings-chip mailings-chip--${tile.tone}`}
          data-testid={`mailings-consent-chip-${tile.key}`}
          title={tile.hint}
        >
          <span className="mailings-chip-value">{tile.value}</span>
          <span className="mailings-chip-label">{tile.label}</span>
        </div>
      ))}
    </section>
  );
}

function CapabilitiesLine() {
  const capabilities = useMailingsCapabilities();
  const value: MailingsCapabilitiesDto | undefined = capabilities.data?.data;
  return (
    <p className="mailings-capabilities" data-testid="mailings-consent-capabilities">
      {formatCapabilities(value)}
    </p>
  );
}

function TestSendCard({ rows }: { rows: MailingConsentRecipientDto[] }) {
  const [applicationId, setApplicationId] = useState<number | null>(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const testSend = useMailingConsentTestSend();
  const selected = rows.find((row) => row.applicationId === applicationId) ?? rows[0] ?? null;

  const submit = () => {
    if (!selected || !email.trim()) return;
    setResult(null);
    testSend.mutate(
      { applicationId: selected.applicationId, email: email.trim(), ...(name.trim() ? { name: name.trim() } : {}) },
      {
        onSuccess: (answer) =>
          setResult(
            `Письмо ушло на ${answer.data.to} (личный кабинет: ${answer.data.startUrl}; страница курса: ${
              answer.data.gosuslugiTestLink ?? 'не определилась'
            })`,
          ),
        onError: (error) => setResult(`Ошибка: ${error.message}`),
      },
    );
  };

  return (
    <section className="mailings-test card" data-testid="mailings-consent-test">
      <h2>Письмо тестом</h2>
      <p className="mailings-test-hint">
        Одно письмо на указанный адрес — с именем и ссылкой, как их увидит получатель. Тест не попадает в историю
        рассылки и не мешает «Отправить всем».
      </p>
      <div className="mailings-test-form">
        <Field label="Ребёнок (письмо его данными)" htmlFor="mailings-consent-test-child">
          <Select
            id="mailings-consent-test-child"
            value={selected ? String(selected.applicationId) : ''}
            onChange={(event) => setApplicationId(Number(event.target.value))}
            data-testid="mailings-consent-test-child"
          >
            {rows.map((row) => (
              <option key={row.applicationId} value={row.applicationId}>
                {row.fullName ?? '(без ФИО)'} — {row.renderReady ? 'готов' : 'не готов'}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Имя в письме" htmlFor="mailings-consent-test-name">
          <Input
            id="mailings-consent-test-name"
            value={name}
            placeholder={selected?.greetingName ?? 'Имя Отчество'}
            onChange={(event) => setName(event.target.value)}
            data-testid="mailings-consent-test-name"
          />
        </Field>
        <Field label="Куда отправить" htmlFor="mailings-consent-test-email">
          <Input
            id="mailings-consent-test-email"
            type="email"
            value={email}
            placeholder="ваш@ismart.org"
            onChange={(event) => setEmail(event.target.value)}
            data-testid="mailings-consent-test-email"
          />
        </Field>
        <Button onClick={submit} disabled={!email.trim() || !selected || testSend.isPending} data-testid="mailings-consent-test-send">
          {testSend.isPending ? 'Отправляем…' : 'Отправить письмо тестом'}
        </Button>
      </div>
      {result ? (
        <p className="mailings-test-result" data-testid="mailings-consent-test-result">
          {result}
        </p>
      ) : null}
    </section>
  );
}

function SendAllCard({
  runStatus,
  notSent,
  notReady,
}: {
  runStatus: MailingConsentRunStatusDto | null;
  notSent: number;
  notReady: number;
}) {
  const [confirming, setConfirming] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const sendAll = useMailingConsentSendAll();
  const running = runStatus?.running ?? false;
  const run = runStatus?.run ?? null;

  const arm = () => {
    setConfirming(false);
    setNotice(null);
    sendAll.mutate(undefined, {
      onSuccess: (answer) => setNotice(`Рассылка запущена: ${answer.data.total} получателей (запуск ${answer.data.runId}).`),
      onError: (error) => setNotice(`Не удалось запустить: ${error.message}`),
    });
  };

  return (
    <section className="mailings-sendall card" data-testid="mailings-consent-sendall">
      <h2>Отправить всем</h2>
      <p className="mailings-sendall-hint">
        Письма уходят по очереди, в заданном темпе (по умолчанию 5 в секунду). Запуск продолжается с того места, где остановился: кто
        уже получил это письмо — повторно не получает. Не готовы к отправке: {notReady}.
      </p>
      <div className="mailings-sendall-actions">
        <Button variant="primary" disabled={running || sendAll.isPending} onClick={() => setConfirming(true)} data-testid="mailings-consent-send-all">
          {running ? 'Отправляется…' : `Отправить всем (${notSent})`}
        </Button>
        <RecheckButton />
      </div>
      {running && run ? (
        <p className="mailings-run-progress" data-testid="mailings-consent-run-progress">
          {formatConsentRunProgress(run)}
        </p>
      ) : null}
      {notice ? (
        <p className="mailings-sendall-notice" data-testid="mailings-consent-sendall-notice">
          {notice}
        </p>
      ) : null}
      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        title="Отправить всем?"
        description={`Будет отправлено ${notSent} писем родителям детей от 14 до 17 лет, чей договор подписан без отметки о согласии законного представителя. Тем, кто уже получил это письмо, повторно не отправляем.`}
        footer={
          <>
            <Button onClick={() => setConfirming(false)}>Отмена</Button>
            <Button variant="primary" onClick={arm} data-testid="mailings-consent-send-all-confirm">
              Отправить
            </Button>
          </>
        }
      >
        <p>{LOGIC_DESCRIPTION_CONSENT}</p>
      </Modal>
    </section>
  );
}

function RecheckButton() {
  const recheck = useMailingConsentRecheck();
  const [notice, setNotice] = useState<string | null>(null);
  return (
    <span className="mailings-recheck">
      <Button
        disabled={recheck.isPending}
        onClick={() =>
          recheck.mutate(undefined, {
            onSuccess: (answer) =>
              setNotice(
                `Проверено: ${answer.data.checked}; доставлено: ${answer.data.delivered}; без подтверждения: ${answer.data.unchanged}`,
              ),
            onError: (error) => setNotice(`Не удалось проверить: ${error.message}`),
          })
        }
        data-testid="mailings-consent-recheck"
      >
        Проверить доставку
      </Button>
      {notice ? (
        <span className="mailings-recheck-notice" data-testid="mailings-consent-recheck-notice">
          {notice}
        </span>
      ) : null}
    </span>
  );
}

/** The recipients table — the second panel's chrome, this panel's columns: the
 * «Готовность» badge (name + e-mail) and the track the letter's link follows. */
function RecipientsTable({
  rows,
  page,
  total,
  onPage,
}: {
  rows: MailingConsentRecipientDto[];
  page: number;
  total: number;
  onPage: (next: number) => void;
}) {
  const [previewId, setPreviewId] = useState<number | null>(null);
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);
  return (
    <>
      <TableWrap>
        <table className="data mailings-table" data-testid="mailings-consent-table">
          <thead>
            <tr>
              <th>Ребёнок</th>
              <th>E-mail</th>
              <th>Программа</th>
              <th>Трек</th>
              <th>Готовность</th>
              <th>Отправлено</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.applicationId}
                className="mailings-row"
                onClick={() => setPreviewId(row.applicationId)}
                data-testid="mailings-consent-row"
              >
                <td className="cell-primary">{row.fullName ?? '—'}</td>
                <td className="cell-muted">{row.email ?? '—'}</td>
                <td className="mailings-program" title={row.programName ?? undefined}>
                  {row.programName ?? '—'}
                </td>
                <td className="cell-compact">
                  {TRACK_LABELS[row.track]}
                  {!row.trackResolved ? <span className="cell-muted"> (не определён)</span> : null}
                </td>
                <td className="cell-compact">
                  {row.renderReady ? (
                    <Badge tone="ok">готов</Badge>
                  ) : (
                    <Badge
                      tone="warn"
                      title={
                        row.gosuslugiTestLink
                          ? 'Нет e-mail или нет разобранного имени'
                          : 'Не определилась ссылка на курс на Госуслугах (аудитория) — письмо не уйдёт'
                      }
                    >
                      не готов
                    </Badge>
                  )}
                </td>
                <td className="cell-compact cell-muted">{formatLastSend(row)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrap>
      <Pagination
        meta={{ page, totalPages: pages, total, limit: PAGE_SIZE }}
        onPage={onPage}
        rangeCaption={`Показаны ${from}–${to} из ${total}`}
      />
      <PreviewModal applicationId={previewId} onClose={() => setPreviewId(null)} />
    </>
  );
}

function PreviewModal({ applicationId, onClose }: { applicationId: number | null; onClose: () => void }) {
  const preview = useMailingConsentPreview(applicationId);
  const letter = preview.data?.data ?? null;
  return (
    <Modal open={applicationId !== null} onClose={onClose} title="Предпросмотр письма" wide>
      {preview.isPending || !letter ? (
        <Spinner label="Готовим предпросмотр" />
      ) : (
        <div className="mailings-preview" data-testid="mailings-consent-preview">
          <p className="mailings-preview-meta">
            {letter.subject} · трек {TRACK_LABELS[letter.track]} · личный кабинет {letter.startUrl} · страница курса{' '}
            {letter.gosuslugiTestLink ?? '—'}
          </p>
          {/* The letter is third-party HTML: sandboxed with NO scripts and NO same-origin. */}
          <iframe
            className="mailings-preview-frame"
            title="Предпросмотр письма"
            sandbox=""
            srcDoc={letter.html}
            data-testid="mailings-consent-preview-frame"
          />
          <div className="mailings-history">
            <h3>История писем этому ребёнку</h3>
            {letter.history.length === 0 ? (
              <p data-testid="mailings-consent-history-empty">Ещё не отправлялось.</p>
            ) : (
              <ul data-testid="mailings-consent-history">
                {letter.history.map((message) => (
                  <li key={message.id}>{formatHistoryLine(message)}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
